import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Search, Shield, Plus, Minus, RotateCcw } from "lucide-react";
import { cn } from "@/lib/utils";

const MODULE_LABELS = {
  overview: "Overview", returns: "Retururi", service: "Service", orders: "Comenzi",
  delivery: "Livrări",
  billing: "Facturare", analytics: "Analytics", products: "Produse",
  inventory: "Stocuri", ai: "AI Assistant", ads: "Ads Performance",
  sms: "SMS", email: "Email Marketing", users: "Utilizatori", audit: "Audit Log",
  shopify: "Shopify",
};

/**
 * Permission editor for a single user.
 *
 * Backend model: role gives the default set, then per-user
 *   added_permissions[]  → granted on top
 *   removed_permissions[] → revoked from the defaults
 * effective = (role_defaults ∪ added) − removed
 */
export default function PermissionEditorDialog({ open, onOpenChange, user, onSaved }) {
  const [catalog, setCatalog] = useState(null);
  const [added, setAdded] = useState([]);
  const [removed, setRemoved] = useState([]);
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/permissions/catalog").then((r) => setCatalog(r.data)).catch(() => {});
  }, [open]);

  useEffect(() => {
    if (user) {
      setAdded(user.added_permissions || []);
      setRemoved(user.removed_permissions || []);
      setSearch("");
    }
  }, [user, open]);

  const defaults = useMemo(() => {
    if (!catalog || !user) return new Set();
    return new Set(catalog.role_defaults[user.role] || []);
  }, [catalog, user]);

  const effective = useMemo(() => {
    const s = new Set(defaults);
    added.forEach((p) => s.add(p));
    removed.forEach((p) => s.delete(p));
    return s;
  }, [defaults, added, removed]);

  const groups = useMemo(() => {
    if (!catalog) return [];
    const q = search.trim().toLowerCase();
    return Object.entries(catalog.by_module).map(([mod, perms]) => ({
      module: mod,
      label: MODULE_LABELS[mod] || mod,
      perms: perms.filter((p) =>
        !q || p.label.toLowerCase().includes(q) || p.id.toLowerCase().includes(q) || (MODULE_LABELS[mod] || mod).toLowerCase().includes(q),
      ),
    })).filter((g) => g.perms.length);
  }, [catalog, search]);

  const togglePerm = (id) => {
    const isDefault = defaults.has(id);
    const has = effective.has(id);
    if (has) {
      // Want to remove. If it's a default → put in removed[]. Else → drop from added[].
      if (isDefault) {
        setRemoved((r) => [...new Set([...r, id])]);
        setAdded((a) => a.filter((x) => x !== id));
      } else {
        setAdded((a) => a.filter((x) => x !== id));
      }
    } else {
      // Want to add. If it's a default that was removed → unremove.
      if (isDefault) {
        setRemoved((r) => r.filter((x) => x !== id));
      } else {
        setAdded((a) => [...new Set([...a, id])]);
      }
    }
  };

  const toggleModule = (mod, on) => {
    if (!catalog) return;
    const ids = (catalog.by_module[mod] || []).map((p) => p.id);
    if (on) {
      ids.forEach((id) => {
        if (!effective.has(id)) togglePerm(id);
      });
    } else {
      ids.forEach((id) => {
        if (effective.has(id)) togglePerm(id);
      });
    }
  };

  const resetToRole = () => {
    setAdded([]);
    setRemoved([]);
  };

  const save = async () => {
    if (!user) return;
    setSaving(true);
    try {
      await api.put(`/users/${user.id}/permissions`, {
        added_permissions: added,
        removed_permissions: removed,
      });
      toast.success("Permisiuni salvate");
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (!user) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-hidden flex flex-col" data-testid="permission-editor">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="h-4 w-4 text-primary" />
            Permisiuni · {user.name}
          </DialogTitle>
          <DialogDescription asChild>
            <div className="text-sm text-muted-foreground">
              Rol curent: <Badge variant="outline" className="ml-1">{user.role}</Badge>
              <span className="ml-3 text-xs">
                {effective.size} permisiuni active
                {added.length > 0 && <span className="text-emerald-600 ml-2">+{added.length} adăugate</span>}
                {removed.length > 0 && <span className="text-rose-600 ml-2">−{removed.length} scoase</span>}
              </span>
            </div>
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-2 mb-2">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Caută permisiuni…"
              className="pl-8 h-9"
              data-testid="permissions-search"
            />
          </div>
          <Button variant="outline" size="sm" onClick={resetToRole} data-testid="reset-role-btn">
            <RotateCcw className="h-3.5 w-3.5 mr-1" /> Resetează la rol
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto space-y-3 pr-1">
          {!catalog && <div className="text-sm text-muted-foreground py-6 text-center">Se încarcă catalogul…</div>}
          {groups.map((g) => {
            const total = g.perms.length;
            const active = g.perms.filter((p) => effective.has(p.id)).length;
            const allOn = active === total;
            return (
              <div key={g.module} className="rounded-lg border border-border bg-white" data-testid={`perm-group-${g.module}`}>
                <div className="flex items-center justify-between px-3 py-2 border-b border-border bg-zinc-50/50">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground">Modul</span>
                    <span className="text-sm font-semibold">{g.label}</span>
                    <span className="text-[11px] text-muted-foreground tabular-nums">{active}/{total}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={() => toggleModule(g.module, true)} data-testid={`select-all-${g.module}`}>
                      <Plus className="h-3 w-3" /> toate
                    </Button>
                    <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={() => toggleModule(g.module, false)} data-testid={`deselect-all-${g.module}`}>
                      <Minus className="h-3 w-3" /> niciuna
                    </Button>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 p-2">
                  {g.perms.map((p) => {
                    const has = effective.has(p.id);
                    const isDefault = defaults.has(p.id);
                    const isExtra = !isDefault && has;
                    const isStripped = isDefault && !has;
                    return (
                      <label
                        key={p.id}
                        className={cn(
                          "flex items-center gap-2 px-2 py-1.5 rounded-md cursor-pointer hover:bg-zinc-50 transition",
                          isExtra && "bg-emerald-50/60",
                          isStripped && "bg-rose-50/60",
                        )}
                        data-testid={`perm-${p.id}`}
                      >
                        <Checkbox checked={has} onCheckedChange={() => togglePerm(p.id)} />
                        <div className="min-w-0 flex-1">
                          <div className="text-sm leading-tight">{p.label}</div>
                          <div className="text-[10px] text-muted-foreground font-mono">{p.id}</div>
                        </div>
                        {isExtra && <Badge variant="outline" className="text-[9px] border-emerald-200 text-emerald-700 bg-emerald-50">extra</Badge>}
                        {isStripped && <Badge variant="outline" className="text-[9px] border-rose-200 text-rose-700 bg-rose-50">scos</Badge>}
                      </label>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Anulează</Button>
          <Button onClick={save} disabled={saving} data-testid="save-permissions-btn">
            {saving ? "Se salvează…" : "Salvează permisiuni"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
